// Validation guide — visible to @eureka.ai accounts only.
// One section per dashboard page: key metric, formula, API spot-check,
// BigQuery SQL, and a sanity-constraint table.
import { useState } from 'react';

const TEAL    = '#1DB89A';
const TEAL_BG = 'rgba(29,184,154,0.08)';
const AMBER   = '#f59e0b';
const RED     = '#ef4444';
const BLUE    = '#378ADD';
const BORDER  = 'var(--border)';
const TEXT    = 'var(--text-primary)';
const MUTED   = 'var(--text-muted)';
const CARD    = 'var(--bg-card)';
const BASE    = 'var(--bg-base)';
const MONO    = '"SF Mono","Fira Code","Fira Mono",monospace';

// ── Shared primitives ─────────────────────────────────────────────────────────

function SectionTag({ children }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase',
      color: TEAL, background: TEAL_BG, padding: '2px 8px', borderRadius: 4,
    }}>{children}</span>
  );
}

function FormulaBox({ rows }) {
  return (
    <div style={{
      background: BASE, border: `1px solid ${BORDER}`, borderRadius: 10,
      padding: '14px 18px', fontFamily: MONO, fontSize: 13, lineHeight: 2,
      margin: '12px 0',
    }}>
      {rows.map((row, i) => (
        <div key={i}>
          {i > 0 && <div style={{ borderTop: `1px dashed ${BORDER}`, margin: '6px 0' }} />}
          <div style={{ fontSize: 10, fontFamily: 'inherit', fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: MUTED, marginBottom: 2 }}>
            {row.label}
          </div>
          <div>
            <span style={{ color: TEAL, fontWeight: 700 }}>{row.lhs}</span>
            <span style={{ color: MUTED }}> = </span>
            <span style={{ color: TEXT }}>{row.rhs}</span>
            {row.note && <span style={{ fontSize: 11, color: MUTED, marginLeft: 12, fontFamily: MONO }}>{row.note}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function SqlBlock({ sql }) {
  return (
    <pre style={{
      background: '#0f172a', color: '#e2e8f0', borderRadius: 10,
      padding: '14px 18px', fontSize: 12.5, lineHeight: 1.75,
      overflow: 'auto', margin: '12px 0', fontFamily: MONO,
      whiteSpace: 'pre-wrap', wordBreak: 'break-all',
    }}>{sql}</pre>
  );
}

function ApiLine({ method = 'GET', path, params }) {
  return (
    <div style={{
      background: BASE, border: `1px solid ${BORDER}`, borderRadius: 8,
      padding: '10px 14px', margin: '12px 0', fontFamily: MONO, fontSize: 12.5,
    }}>
      <span style={{ color: '#16a34a', fontWeight: 700, marginRight: 8 }}>{method}</span>
      <span style={{ color: BLUE }}>{path}</span>
      {params && (
        <div style={{ marginTop: 6, color: MUTED, fontSize: 12 }}>
          {params.map((p, i) => (
            <div key={i}>
              <span style={{ color: AMBER }}>{p.name}</span>
              <span style={{ color: MUTED }}>=</span>
              <span style={{ color: TEXT }}>{p.value}</span>
              {p.note && <span style={{ color: MUTED, marginLeft: 8 }}>← {p.note}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SanityTable({ rows }) {
  const STATUS = {
    pass:  { label: '✓ Pass',  bg: '#f0fdf4', text: '#16a34a' },
    warn:  { label: '⚠ Warn',  bg: '#fefce8', text: '#ca8a04' },
    fail:  { label: '✗ Fail',  bg: '#fef2f2', text: RED },
  };
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, margin: '12px 0' }}>
      <thead>
        <tr style={{ background: TEAL }}>
          {['Check', 'Expected', 'If violated'].map(h => (
            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: '#fff', fontWeight: 600 }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{ background: i % 2 === 0 ? BASE : CARD, borderBottom: `1px solid ${BORDER}` }}>
            <td style={{ padding: '7px 12px', fontWeight: 500, color: TEXT }}>{r.check}</td>
            <td style={{ padding: '7px 12px', color: MUTED, fontFamily: MONO, fontSize: 12 }}>{r.expected}</td>
            <td style={{ padding: '7px 12px', color: RED, fontSize: 12 }}>{r.violation}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CrossRef({ refs }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '8px 0' }}>
      {refs.map(r => (
        <span key={r} style={{
          background: TEAL_BG, color: TEAL, fontSize: 11.5, fontWeight: 600,
          padding: '3px 10px', borderRadius: 20, border: `1px solid ${TEAL}30`,
        }}>{r}</span>
      ))}
    </div>
  );
}

// ── Section definitions ───────────────────────────────────────────────────────

const SECTIONS = [
  {
    id: 'panel-representativeness',
    label: 'Panel Representativeness',
    group: 'Category',
    what: 'Compares the VMO2 panel demographic composition (rolling 30-day, end-of-month) against UK census data to assess how representative the panel is of the UK population. No category filter — pure panel data only. Geography charts exclude Belfast (BT) and Unknown.',
    formulas: [
      { label: 'Panel universe (segment)', lhs: 'universe', rhs: 'rolling_30d_unique_customers  (month-end value from omni_panel_size, per attribute/attribute_value)' },
      { label: 'Panel share %', lhs: 'panel_share_pct', rhs: 'segment_universe / overall_universe × 100' },
      { label: 'Census share %', lhs: 'census_share_pct', rhs: 'hardcoded ONS constant from census.js  (age/gender: 2021 Census; geo: mid-2023 LA estimates)' },
      { label: 'Panel coverage', lhs: 'coverage_pct', rhs: 'overall_universe / 67,500,000 × 100  (UK population ≈ 67.5M)' },
      { label: 'Panel index', lhs: 'panel_index', rhs: '(panel_share_pct / census_share_pct) × 100  →  100 = census parity' },
    ],
    api: { path: '/api/v1/panel/demographics', params: [{ name: 'month_id', value: 'YYYY-MM' }] },
    sql: `-- Verify universe for overall and age bands for a month
SELECT
  attribute,
  attribute_value,
  ARRAY_AGG(rolling_30d_unique_customers ORDER BY visited_date DESC LIMIT 1)[OFFSET(0)] AS universe
FROM \`tsi-explorer.vmo2_database_uk.omni_panel_size\`
WHERE DATE_TRUNC(visited_date, MONTH) = DATE('2026-04-01')
  AND attribute IN ('overall', 'age_band', 'gender', 'postcode_area')
GROUP BY 1, 2
ORDER BY 1, universe DESC;

-- Spot-check panel share % and index for age band 18-24
-- census_share for 18-24 = 10.81% (ONS 2021 Census, UK adults 18+)
WITH overall AS (
  SELECT ARRAY_AGG(rolling_30d_unique_customers ORDER BY visited_date DESC LIMIT 1)[OFFSET(0)] AS total
  FROM \`tsi-explorer.vmo2_database_uk.omni_panel_size\`
  WHERE DATE_TRUNC(visited_date, MONTH) = DATE('2026-04-01')
    AND attribute = 'overall' AND attribute_value = 'all'
),
seg AS (
  SELECT ARRAY_AGG(rolling_30d_unique_customers ORDER BY visited_date DESC LIMIT 1)[OFFSET(0)] AS seg
  FROM \`tsi-explorer.vmo2_database_uk.omni_panel_size\`
  WHERE DATE_TRUNC(visited_date, MONTH) = DATE('2026-04-01')
    AND attribute = 'age_band' AND attribute_value = '18-24'
)
SELECT
  ROUND(seg.seg / overall.total * 100, 2)        AS panel_share_pct,
  10.81                                           AS census_share_pct,
  ROUND((seg.seg / overall.total * 100) / 10.81 * 100) AS panel_index
FROM seg CROSS JOIN overall;`,
    sanity: [
      { check: 'overall_universe = SUM of all age_band universes', expected: 'Approximately (rounding OK)', violation: 'Large discrepancy = attribute definition overlap or missing segments' },
      { check: 'panel_share_pct for all age bands sums to ~100%', expected: '~100% (excluding Unknown)', violation: 'Segments missing or duplicated in source table' },
      { check: 'Belfast (BT) absent from geo charts', expected: 'Excluded by filter', violation: 'If visible, BT filter not applied — distorts representativeness picture' },
      { check: 'panel_index between 50 and 200 for most segments', expected: 'Typical range', violation: 'Index outside this range = extreme over/under-representation — investigate data pipeline or census constant' },
      { check: 'coverage_pct between 10% and 25%', expected: 'Typical panel coverage range', violation: 'Outside range = overall universe figure incorrect or wrong month selected' },
      { check: 'Gender split: M + F sums to ~100% of panel', expected: 'M ~47-50%, F ~50-53%', violation: 'Large U (Unknown) share = gender attribution issue in panel data' },
    ],
    crossRef: ['Panel Size', 'Category Penetration (all)'],
  },

  {
    id: 'panel-size',
    label: 'Panel Size',
    group: 'Category',
    what: 'Rolling 30-day unique O2 customers (panel_size) and how many are active category users (cat_users) — the denominator for all penetration calculations. Demographic charts default to Index vs Census mode: each bar shows how the panel or category composition compares to UK census proportions (index 100 = parity). Toggle to Absolute to see raw counts.',
    formulas: [
      { label: 'Panel universe', lhs: 'panel_size', rhs: 'rolling_30d_unique_customers  (month-end value from omni_panel_size)' },
      { label: 'Category users', lhs: 'cat_users', rhs: 'SUM(active_users) from omni_by_age WHERE application = \'Any App\'' },
      { label: 'Category penetration', lhs: 'category_share_pct', rhs: 'cat_users / panel_size × 100' },
      { label: 'Panel index vs census', lhs: 'panel_index', rhs: '(segment_universe / overall_universe) / census_share × 100 × 100', note: 'census_share from ONS 2021 Census (age/gender) or ONS mid-2023 estimates (geo)' },
      { label: 'Category index vs census', lhs: 'cat_index', rhs: '(segment_cat_users / overall_cat_users) / census_share × 100 × 100', note: 'same census denominator as panel_index' },
    ],
    api: { path: '/api/v1/panel/overview', params: [{ name: 'month_id', value: 'YYYY-MM' }, { name: 'category', value: 'Ride Hailing' }, { name: 'session_type', value: 'open_app' }] },
    sql: `-- Verify panel_size for a month
SELECT
  attribute, attribute_value,
  ARRAY_AGG(rolling_30d_unique_customers ORDER BY visited_date DESC LIMIT 1)[OFFSET(0)] AS panel_size
FROM \`tsi-explorer.vmo2_database_uk.omni_panel_size\`
WHERE DATE_TRUNC(visited_date, MONTH) = DATE('2025-11-01')
  AND attribute = 'overall'
GROUP BY 1, 2;

-- Verify cat_users
SELECT SUM(active_users) AS cat_users
FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_age\`
WHERE month_id = '2025-11'
  AND session_type = 'open_app'
  AND application  = 'Any App'
  AND category     = 'Ride Hailing';

-- Spot-check panel index for age band 18-24
-- census_share for 18-24 = 10.81% (ONS 2021 Census, UK adults 18+)
WITH overall AS (
  SELECT ARRAY_AGG(rolling_30d_unique_customers ORDER BY visited_date DESC LIMIT 1)[OFFSET(0)] AS total_universe
  FROM \`tsi-explorer.vmo2_database_uk.omni_panel_size\`
  WHERE DATE_TRUNC(visited_date, MONTH) = DATE('2025-11-01')
    AND attribute = 'overall' AND attribute_value = 'all'
),
seg AS (
  SELECT ARRAY_AGG(rolling_30d_unique_customers ORDER BY visited_date DESC LIMIT 1)[OFFSET(0)] AS seg_universe
  FROM \`tsi-explorer.vmo2_database_uk.omni_panel_size\`
  WHERE DATE_TRUNC(visited_date, MONTH) = DATE('2025-11-01')
    AND attribute = 'age_band' AND attribute_value = '18-24'
)
SELECT
  ROUND((seg.seg_universe / overall.total_universe * 100) / 10.81 * 100) AS panel_index_18_24
FROM seg CROSS JOIN overall;`,
    sanity: [
      { check: 'panel_size > cat_users', expected: 'Always', violation: 'Category users cannot exceed the total panel — filter or join bug' },
      { check: 'category_share_pct between 0 and 100', expected: '0–100%', violation: 'Denominator mismatch or wrong month filter' },
      { check: 'panel_size stable month-over-month', expected: '±5% typical', violation: 'Sharp spike/drop = data pipeline issue in omni_panel_size' },
      { check: 'Overall panel_size ≥ any demographic segment panel_size', expected: 'Always', violation: 'Segment panel exceeds total — segment definition overlaps with overall' },
      { check: 'panel_index for all segments close to 100 on average', expected: '~100 weighted average (panel broadly representative)', violation: 'Extreme skew across all segments = panel fundamentally unrepresentative of UK population' },
      { check: 'Unknown / U segments absent from index charts', expected: 'Filtered out (no census equivalent)', violation: 'If shown, census_share denominator is null — division-by-zero or NaN bar' },
    ],
    crossRef: ['Category Behaviour', 'Category Penetration (all)', 'Brand Penetration (all)'],
  },

  {
    id: 'category-behaviour',
    label: 'Category Behaviour',
    group: 'Category',
    what: 'Monthly active users, session counts, and penetration for the category as a whole. Uses "Any App" rows — deduplicated so a user on 2 apps counts once.',
    formulas: [
      { label: 'Active users', lhs: 'active_users', rhs: 'SUM(active_users) from omni_by_age WHERE application = \'Any App\'' },
      { label: 'Penetration', lhs: 'penetration_pct', rhs: 'active_users / panel_size × 100' },
      { label: 'Session counts', lhs: 'total_15min_sessions', rhs: 'SUM(total_15min_sessions) — all qualifying sessions across all age bands' },
    ],
    api: { path: '/api/v1/category/behaviour-trend', params: [{ name: 'category', value: 'Ride Hailing' }, { name: 'session_type', value: 'open_app' }] },
    sql: `SELECT
  month_id,
  SUM(active_users)            AS active_users,
  SUM(total_15min_sessions)    AS sessions_15min
FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_age\`
WHERE session_type = 'open_app'
  AND application  = 'Any App'
  AND category     = 'Ride Hailing'
GROUP BY 1
ORDER BY 1;`,
    sanity: [
      { check: 'active_users ≤ panel_size', expected: 'Always', violation: 'Category users exceed total panel — deduplication or join error' },
      { check: 'total_15min_sessions ≥ active_users', expected: 'Always', violation: 'Fewer sessions than users — impossible if each user has ≥1 session' },
      { check: 'Month-on-month active_users change < 30%', expected: 'Typical ±5–15%', violation: 'Large jump/drop may indicate data gap rather than real behaviour change' },
    ],
    crossRef: ['Panel Size', 'Monthly Trends — Nation', 'Category Penetration (all)'],
  },

  {
    id: 'trends',
    label: 'Monthly Trends',
    group: 'Category',
    what: 'Per-app monthly time series for active users and sessions, broken down by Age, Gender, Geography, and Intensity. "Any App" rows are the deduplicated category total.',
    formulas: [
      { label: 'Any App (category total)', lhs: 'active_users', rhs: 'COUNT(DISTINCT user_id) across all category apps in the month', note: '(deduplicated)' },
      { label: 'Per-app', lhs: 'active_users', rhs: 'COUNT(DISTINCT user_id) WHERE app = X in the month', note: '(not deduplicated across apps)' },
    ],
    api: { path: '/api/v1/age/trend', params: [{ name: 'category', value: 'Ride Hailing' }, { name: 'session_type', value: 'open_app', note: 'returns full monthly history — no month_id filter; pivot on "age" to get per-band series' }] },
    sql: `-- Nation-level trend: Any App vs individual brands
SELECT
  month_id,
  application,
  SUM(active_users) AS active_users
FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_age\`
WHERE session_type = 'open_app'
  AND category     = 'Ride Hailing'
GROUP BY 1, 2
ORDER BY 1, application;

-- Cross-check: sum of per-app users vs Any App in same month
SELECT
  month_id,
  SUM(CASE WHEN application != 'Any App' THEN active_users ELSE 0 END) AS sum_per_app,
  MAX(CASE WHEN application  = 'Any App' THEN active_users END)         AS any_app
FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_age\`
WHERE session_type = 'open_app' AND category = 'Ride Hailing'
GROUP BY 1 ORDER BY 1;`,
    sanity: [
      { check: 'Any App active_users ≤ SUM(per-app active_users)', expected: 'Always — deduplication means Any App ≤ sum', violation: 'Any App > sum = deduplication is broken; impossible' },
      { check: 'Any App active_users ≥ each individual brand', expected: 'Always', violation: 'A brand has more users than the category total — category filter missing' },
      { check: 'No missing months in the 6-month window', expected: 'Continuous monthly series', violation: 'Gap in series = upstream data pipeline missed a month (Oct 2025 known gap)' },
      { check: 'Geo trend top-N postcodes stable', expected: 'Top 5–10 consistent quarter-to-quarter', violation: 'New postcode suddenly #1 = geo mapping changed or data error' },
    ],
    crossRef: ['Category Behaviour', 'Customer Profiles (Age/Gender/Geo)'],
  },

  {
    id: 'penetration-category',
    label: 'Category Penetration',
    group: 'Category',
    what: 'Penetration of the category within each demographic segment — what % of each age band / gender / postcode area is a category buyer.',
    formulas: [
      { label: 'Segment penetration', lhs: 'penetration_pct', rhs: 'segment_active_users / segment_panel_size × 100' },
      { label: 'Segment active users', lhs: 'segment_active_users', rhs: 'SUM(active_users) from omni_by_age WHERE application=\'Any App\' AND age=\'18-24\'' },
      { label: 'Segment panel', lhs: 'segment_panel_size', rhs: 'rolling_30d_unique_customers from omni_panel_size WHERE attribute=\'age_band\' AND attribute_value=\'18-24\'' },
    ],
    api: { path: '/api/v1/category/penetration-by-age', params: [{ name: 'category', value: 'Ride Hailing' }, { name: 'session_type', value: 'open_app' }, { name: 'month_id', value: 'YYYY-MM' }] },
    sql: `-- Category penetration by age band — manual calculation
WITH panel AS (
  SELECT attribute_value AS age_band,
    ARRAY_AGG(rolling_30d_unique_customers ORDER BY visited_date DESC LIMIT 1)[OFFSET(0)] AS seg_panel
  FROM \`tsi-explorer.vmo2_database_uk.omni_panel_size\`
  WHERE DATE_TRUNC(visited_date, MONTH) = DATE('2025-11-01')
    AND attribute = 'age_band'
  GROUP BY 1
),
users AS (
  SELECT age AS age_band, SUM(active_users) AS seg_users
  FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_age\`
  WHERE month_id = '2025-11' AND session_type = 'open_app'
    AND application = 'Any App' AND category = 'Ride Hailing'
  GROUP BY 1
)
SELECT u.age_band, u.seg_users, p.seg_panel,
  ROUND(SAFE_DIVIDE(u.seg_users, p.seg_panel) * 100, 2) AS penetration_pct
FROM users u JOIN panel p USING (age_band)
ORDER BY penetration_pct DESC;`,
    sanity: [
      { check: 'penetration_pct between 0 and 100', expected: '0–100%', violation: 'segment_users > segment_panel — segment definition mismatch' },
      { check: 'Sum of (segment_panel × segment_penetration) ≈ overall_users × 100', expected: 'Weighted average = overall penetration', violation: 'Large deviation = segments don\'t add up to overall panel' },
      { check: 'Segment panel_size sums ≤ overall panel_size', expected: 'Segments are non-overlapping within dimension', violation: 'Sum > overall = double counting within a dimension' },
      { check: 'Unknown segment excluded from charts', expected: 'Unknown excluded per convention', violation: 'If shown, inflates denominator and deflates penetration' },
    ],
    crossRef: ['Panel Size', 'Brand Penetration (Geo/Age/Gender)', 'Growth Opportunities'],
  },

  {
    id: 'share-penetration',
    label: 'Share & Brand Penetration',
    group: 'Brand',
    what: 'Market share (brand users as % of category users) and national brand penetration (brand users as % of full panel) for each app.',
    formulas: [
      { label: 'Market share', lhs: 'share_pct', rhs: 'brand_active_users / category_active_users × 100' },
      { label: 'National penetration', lhs: 'national_penetration_pct', rhs: 'brand_active_users / panel_size × 100' },
      { label: 'Brand active users', lhs: 'brand_users', rhs: 'active_users_x from eur_omni__monthly_cross_app WHERE application_x = application_y = \'Uber\'', note: 'diagonal row; cross-check: SUM(active_users) from eur_omni__monthly_by_age WHERE application = \'Uber\' should match' },
    ],
    api: { path: '/api/v1/competition/share', params: [{ name: 'category', value: 'Ride Hailing' }, { name: 'session_type', value: 'open_app' }, { name: 'month_id', value: 'YYYY-MM' }] },
    sql: `-- Brand share and national penetration manual check
WITH cat AS (
  SELECT SUM(active_users) AS cat_users
  FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_age\`
  WHERE month_id = '2025-11' AND session_type = 'open_app'
    AND application = 'Any App' AND category = 'Ride Hailing'
),
brands AS (
  SELECT application, SUM(active_users) AS brand_users
  FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_age\`
  WHERE month_id = '2025-11' AND session_type = 'open_app'
    AND application != 'Any App' AND category = 'Ride Hailing'
  GROUP BY 1
),
panel AS (
  SELECT ARRAY_AGG(rolling_30d_unique_customers ORDER BY visited_date DESC LIMIT 1)[OFFSET(0)] AS panel_size
  FROM \`tsi-explorer.vmo2_database_uk.omni_panel_size\`
  WHERE DATE_TRUNC(visited_date, MONTH) = DATE('2025-11-01')
    AND attribute = 'overall' AND attribute_value = 'all'
)
SELECT
  b.application,
  b.brand_users,
  ROUND(SAFE_DIVIDE(b.brand_users, c.cat_users)  * 100, 2) AS share_pct,
  ROUND(SAFE_DIVIDE(b.brand_users, p.panel_size)  * 100, 2) AS national_penetration_pct
FROM brands b CROSS JOIN cat c CROSS JOIN panel p
ORDER BY share_pct DESC;`,
    sanity: [
      { check: 'share_pct between 0 and 100 per brand', expected: '0–100%', violation: 'brand_users > cat_users — deduplication mismatch between tables' },
      { check: 'national_penetration_pct < share_pct', expected: 'Always (panel ≥ category)', violation: 'National penetration > share = impossible; panel_size < cat_users error' },
      { check: 'SUM(share_pct) may exceed 100%', expected: 'Expected — multi-brand users counted in each brand', violation: 'If SUM < 100%, some category users are attributed to no brand (data gap)' },
      { check: 'Brand users ≤ Any App users for same category', expected: 'Always', violation: 'A brand\'s users exceed the deduplicated category total — category filter error' },
    ],
    crossRef: ['Panel Size', 'Category Behaviour', 'Brand Penetration (Geo/Age/Gender)'],
  },

  {
    id: 'brand-penetration',
    label: 'Brand Penetration by Segment',
    group: 'Brand',
    what: 'Penetration of each brand within demographic segments (age, gender, geography). Mirrors Category Penetration but per-brand.',
    formulas: [
      { label: 'Brand segment penetration', lhs: 'penetration_pct', rhs: 'brand_seg_users / segment_panel_size × 100' },
      { label: 'Gap vs category', lhs: 'gap_pp', rhs: 'category_seg_penetration − brand_seg_penetration', note: 'frontend-derived; API returns penetration_pct per brand — subtract from category penetration_pct to get the gap' },
    ],
    api: { path: '/api/v1/brand/penetration-by-age', params: [{ name: 'category', value: 'Ride Hailing' }, { name: 'session_type', value: 'open_app' }, { name: 'month_id', value: 'YYYY-MM' }] },
    sql: `-- Brand penetration by age: all brands in one query
WITH panel AS (
  SELECT attribute_value AS age_band,
    ARRAY_AGG(rolling_30d_unique_customers ORDER BY visited_date DESC LIMIT 1)[OFFSET(0)] AS seg_panel
  FROM \`tsi-explorer.vmo2_database_uk.omni_panel_size\`
  WHERE DATE_TRUNC(visited_date, MONTH) = DATE('2025-11-01') AND attribute = 'age_band'
  GROUP BY 1
)
SELECT
  age AS age_band, application,
  SUM(active_users) AS brand_users,
  p.seg_panel,
  ROUND(SAFE_DIVIDE(SUM(active_users), p.seg_panel) * 100, 2) AS penetration_pct
FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_age\` b
JOIN panel p ON b.age = p.age_band
WHERE b.month_id = '2025-11' AND b.session_type = 'open_app'
  AND b.application != 'Any App' AND b.category = 'Ride Hailing'
GROUP BY 1, 2, 4
ORDER BY age_band, penetration_pct DESC;`,
    sanity: [
      { check: 'Brand segment penetration ≤ category segment penetration', expected: 'Always', violation: 'A brand penetrates a segment more than the whole category — filter error' },
      { check: 'penetration_pct between 0 and 100', expected: '0–100%', violation: 'Segment panel_size smaller than segment brand_users — wrong panel join' },
      { check: 'National penetration = weighted avg of segment penetrations', expected: 'Approximate match (may differ if Unknown excluded)', violation: 'Large divergence = Unknown segment excluded from segments but included nationally' },
    ],
    crossRef: ['Share & Brand Penetration', 'Category Penetration', 'Growth Opportunities'],
  },

  {
    id: 'customer-profiles',
    label: 'Customer Profiles',
    group: 'Brand',
    what: 'Distribution of brand and category users across Age, Gender, Geography, and Usage Intensity. Shows the composition of each brand\'s buyer base.',
    formulas: [
      { label: 'Age share', lhs: 'age_share_pct', rhs: 'users_in_age_band / total_brand_users × 100' },
      { label: 'Gender split', lhs: 'gender_share_pct', rhs: 'users_in_gender / total_brand_users × 100' },
      { label: 'Intensity (Heavy)', lhs: 'heavy_pct', rhs: 'heavy_users / total_brand_users × 100', note: '(Heavy = top tercile by sessions)' },
    ],
    api: { path: '/api/v1/age/snapshot', params: [{ name: 'category', value: 'Ride Hailing' }, { name: 'session_type', value: 'open_app' }, { name: 'month_id', value: 'YYYY-MM' }] },
    sql: `-- Age composition of each brand
SELECT
  application, age,
  active_users,
  ROUND(SAFE_DIVIDE(active_users,
    SUM(active_users) OVER (PARTITION BY application)) * 100, 1) AS age_share_pct
FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_age\`
WHERE month_id = '2025-11' AND session_type = 'open_app'
  AND application != 'Any App' AND category = 'Ride Hailing'
  AND age NOT IN ('Unknown')
ORDER BY application, age;`,
    sanity: [
      { check: 'SUM(age_share_pct) per brand ≈ 100%', expected: '100% (excluding Unknown)', violation: 'Does not sum to 100 = a band is missing or double-counted' },
      { check: 'Gender: M + F ≈ 100% (excluding Unknown)', expected: 'M + F = ~100%', violation: 'Sum < 100 = Unknown gender excluded from denominator mismatch' },
      { check: 'Intensity: Heavy + Medium + Light ≈ 100%', expected: '~100%', violation: 'Gap = some users classified as Unknown intensity (check upstream thresholds)' },
      { check: 'Category "Any App" profile ≠ any single brand', expected: 'Any App is the weighted average of all brands', violation: 'If identical, likely Any App data is being used as a brand row' },
    ],
    crossRef: ['Monthly Trends (Age/Gender/Geo)', 'Brand Penetration by Segment'],
  },

  {
    id: 'intensity',
    label: 'Usage Intensity',
    group: 'Brand',
    what: 'Classification of users as Heavy, Medium, or Light based on session frequency. Heavy users have the most sessions per month; light users have the fewest.',
    formulas: [
      { label: 'Intensity bands', lhs: 'intensity', rhs: 'Heavy | Medium | Light  (tercile thresholds set upstream)', note: '(Light data gap known in some months — see omni_by_intensity notes)' },
      { label: 'Avg sessions per user', lhs: 'avg_sessions', rhs: 'total_15min_sessions / active_users', note: 'should be highest for Heavy' },
    ],
    api: { path: '/api/v1/intensity/snapshot', params: [{ name: 'category', value: 'Ride Hailing' }, { name: 'session_type', value: 'open_app' }, { name: 'month_id', value: 'YYYY-MM' }] },
    sql: `-- Intensity distribution per brand
SELECT
  application, intensity,
  SUM(active_users)            AS users,
  SUM(total_15min_sessions)    AS sessions_15min,
  ROUND(SAFE_DIVIDE(
    SUM(total_15min_sessions), SUM(active_users)), 1) AS avg_sessions_per_user
FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_intensity\`
WHERE month_id = '2025-11' AND session_type = 'open_app'
  AND category = 'Ride Hailing'
GROUP BY 1, 2
ORDER BY application, intensity;`,
    sanity: [
      { check: 'avg_sessions Heavy > Medium > Light', expected: 'Ordered by definition', violation: 'Out of order = tercile threshold logic inverted upstream' },
      { check: 'Heavy + Medium + Light ≤ Any App active_users', expected: '≤ (deduplication means Any App may be lower)', violation: 'Sum > Any App = intensity rows not properly deduplicated' },
      { check: 'Light user counts present', expected: 'Light should be the largest band by count', violation: 'Missing Light rows = known upstream data gap; flag to data team' },
      { check: 'No negative session counts', expected: 'Sessions ≥ 0', violation: 'Negative = aggregation error in source pipeline' },
    ],
    crossRef: ['Customer Profiles', 'Monthly Trends — Intensity'],
  },

  {
    id: 'loyalty',
    label: 'App Loyalty',
    group: 'Brand',
    what: 'App loyalty classification — how many apps in the category a user used in the month. Solus = used only this app (100% loyal); Dual = used exactly 2 apps; Three+ = used 3 or more apps. Solus users are the brand\'s most valuable — they gave all their category sessions to this app.',
    formulas: [
      { label: 'Loyalty bands', lhs: 'app_loyalty', rhs: 'Solus | Dual | Three+', note: 'based on number of distinct category apps the user used in the month' },
      { label: 'Solus share', lhs: 'solus_pct', rhs: 'users WHERE app_loyalty = \'Solus\' / total_brand_users × 100', note: 'high Solus % = brand retains exclusive users; low = heavy cross-app usage' },
    ],
    api: { path: '/api/v1/loyalty/snapshot', params: [{ name: 'category', value: 'Ride Hailing' }, { name: 'session_type', value: 'open_app' }, { name: 'month_id', value: 'YYYY-MM' }] },
    sql: `SELECT
  application, app_loyalty,
  SUM(active_users)         AS users,
  SUM(total_15min_sessions) AS sessions
FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_app_loyalty\`
WHERE month_id = '2025-11' AND session_type = 'open_app'
  AND category = 'Ride Hailing'
GROUP BY 1, 2
ORDER BY application, app_loyalty;`,
    sanity: [
      { check: 'app_loyalty values are exactly Solus, Dual, Three+', expected: 'Three distinct bands, mutually exclusive', violation: 'Other values or missing bands = loyalty classification changed upstream; check pipeline' },
      { check: 'Solus + Dual + Three+ users ≤ total brand active_users (from omni_by_age)', expected: 'Loyalty bands are a subset of brand users', violation: 'Sum > total brand users = different universe; omni_by_loyalty may include months/session_types not in omni_by_age' },
      { check: 'No "Any App" rows in omni_by_loyalty', expected: 'Loyalty is per-app only (the API filters application != \'Any App\')', violation: 'If Any App appears, it means the pipeline generated an aggregate row that has no meaningful loyalty interpretation' },
    ],
    crossRef: ['Usage Intensity', 'Share of Requirements', 'Customer Profiles'],
  },

  {
    id: 'competition',
    label: 'Duplication Matrix',
    group: 'Competition',
    what: 'Pairwise user overlap between every pair of apps in the category. overlap_pct answers: "Of all users of App X, what % also use App Y?"',
    formulas: [
      { label: 'Overlap %', lhs: 'overlap_pct', rhs: 'active_users_xy / active_users_x × 100', note: 'directional: X→Y ≠ Y→X' },
      { label: 'Absolute overlap', lhs: 'active_users_xy', rhs: 'COUNT(DISTINCT user_id) active in BOTH app_x AND app_y in same month' },
    ],
    api: { path: '/api/v1/competition/overlap', params: [{ name: 'category', value: 'Ride Hailing' }, { name: 'session_type', value: 'open_app' }, { name: 'month_id', value: 'YYYY-MM' }] },
    sql: `SELECT
  application_x, application_y,
  active_users_x, active_users_xy,
  ROUND(SAFE_DIVIDE(active_users_xy, active_users_x) * 100, 1) AS overlap_pct
FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_cross_app\`
WHERE month_id = '2025-11'
  AND session_type = 'open_app'
  AND category = 'Ride Hailing'
  AND application_x != application_y
ORDER BY application_x, overlap_pct DESC;`,
    sanity: [
      { check: 'active_users_xy ≤ MIN(active_users_x, active_users_y)', expected: 'Overlap cannot exceed the smaller brand', violation: 'active_users_xy > active_users_x = directional logic inverted in source pipeline' },
      { check: 'overlap_pct between 0 and 100', expected: '0–100%', violation: '>100% = active_users_x too small or wrong session_type used for denominator' },
      { check: 'Diagonal rows (application_x = application_y) excluded from API response', expected: 'API endpoint filters application_x != application_y', violation: 'If diagonal rows appear, the client-side filter is missing — overlap_pct for self-rows would be 100% by definition but they are excluded by design' },
      { check: 'Large brand shows high overlap with small brand but not vice versa', expected: 'Expected asymmetry (Byron Sharp Double Jeopardy)', violation: 'If symmetric, active_users_x is the same for both directions = join error' },
    ],
    crossRef: ['Share of Requirements', 'Share & Brand Penetration'],
  },

  {
    id: 'sor',
    label: 'Share of Requirements',
    group: 'Competition',
    what: 'What % of the sessions in a brand\'s category are happening in OTHER apps (not the brand itself). Measures how much of the category engagement the brand does NOT capture. High pct_sessions_lost = brand users spend a large share of their category time in competitors.',
    formulas: [
      { label: '% sessions lost', lhs: 'pct_sessions_lost', rhs: '(1 − sessions_x / (sessions_x + Σ off-diagonal sessions_xy)) × 100', note: 'sessions_x = brand\'s own 15min sessions; Σ sessions_xy = sum of brand users\' sessions that went to each other app' },
      { label: 'Cross sessions', lhs: 'Σ sessions_xy', rhs: 'SUM(total_15min_sessions_xy WHERE application_y ≠ application_x)', note: 'from eur_omni__monthly_cross_app — grouped by application_x across all y' },
    ],
    api: { path: '/api/v1/competition/sor-trend', params: [{ name: 'category', value: 'Ride Hailing' }, { name: 'session_type', value: 'open_app', note: 'returns one row per (month_id, application) — no application filter; pivot on application to compare brands' }] },
    sql: `-- Share of Requirements: % of category sessions happening in other apps
-- Mirrors the actual API formula exactly
SELECT
  application_x AS application,
  ANY_VALUE(total_15min_sessions_x)                                     AS sessions_x,
  SUM(IF(application_y != application_x, total_15min_sessions_xy, 0))  AS cross_sessions,
  ROUND((1 - SAFE_DIVIDE(
    ANY_VALUE(total_15min_sessions_x),
    ANY_VALUE(total_15min_sessions_x)
      + SUM(IF(application_y != application_x, total_15min_sessions_xy, 0))
  )) * 100, 2) AS pct_sessions_lost
FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_cross_app\`
WHERE month_id = '2025-11'
  AND session_type = 'open_app'
  AND category = 'Ride Hailing'
GROUP BY 1
ORDER BY pct_sessions_lost DESC;`,
    sanity: [
      { check: 'pct_sessions_lost between 0 and 100', expected: '0–100%', violation: '>100% = impossible mathematically; indicates wrong aggregation — ensure ANY_VALUE(sessions_x) is constant per application_x group' },
      { check: 'High-share brands have lower pct_sessions_lost', expected: 'Byron Sharp: large brands capture more sessions', violation: 'If small brands have lower sessions_lost than large brands — source data aggregation error' },
      { check: 'sessions_x matches total_15min_sessions_x from competition/overlap for same app+month', expected: 'Same scalar (diagonal row value)', violation: 'Mismatch = different session_type used between queries' },
    ],
    crossRef: ['Duplication Matrix', 'Usage Intensity'],
  },

  {
    id: 'social',
    label: 'Social Channel Reach',
    group: 'Channel Preferences',
    what: 'Two-section page. Section ①: Brand vs Category — true reach % of each social platform for the selected brand vs the full category universe. Section ②: Compare Brands — head-to-head reach comparison between Brand A (indigo) and Brand B (amber). reach_pct = active_users_xy / total_app_users. For ALL Universe the denominator intentionally omits the category filter — matching the cross-category scope of the numerator. The Opportunity Index is on a separate page (channel-opp-index).',
    formulas: [
      { label: 'Reach %', lhs: 'reach_pct', rhs: 'active_users_xy / total_app_users × 100', note: 'ALL Universe denominator: Any App across ALL categories (no category filter). Brand denominator: brand users in the selected category.' },
      { label: 'Population reach', lhs: 'pop_reach_pct', rhs: 'active_users_xy / panel_size × 100' },
      { label: 'Reach gap (Section ①)', lhs: 'reach_gap', rhs: 'brand_reach_pct − category_reach_pct', note: 'positive = brand over-indexes; negative = under-indexes' },
      { label: 'Reach gap (Section ②)', lhs: 'reach_gap', rhs: 'brand_b_reach_pct − brand_a_reach_pct' },
    ],
    api: { path: '/api/v1/social/reach', params: [{ name: 'month_id', value: 'YYYY-MM' }, { name: 'category', value: 'Ride Hailing' }, { name: 'application', value: 'ALL Universe', note: 'use brand name for brand-level; called 3× per page load' }] },
    sql: `-- Verify social reach for ALL Universe.
-- Denominator = Any App across ALL categories (no category filter) to match
-- omni_cross_social's cross-category ALL Universe numerator.
WITH app_users AS (
  SELECT SUM(active_users) AS total_users
  FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_age\`
  WHERE month_id = '2025-11' AND session_type = 'open_app'
    AND application = 'Any App'
    -- intentionally no category filter; ALL Universe spans all categories
),
panel AS (
  SELECT ARRAY_AGG(rolling_30d_unique_customers ORDER BY visited_date DESC LIMIT 1)[OFFSET(0)] AS panel_size
  FROM \`tsi-explorer.vmo2_database_uk.omni_panel_size\`
  WHERE DATE_TRUNC(visited_date, MONTH) = DATE('2025-11-01')
    AND attribute = 'overall' AND attribute_value = 'all'
),
social AS (
  SELECT channel_y, SUM(active_users_xy) AS active_users_xy
  FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_social_overlap\`
  WHERE month_id = '2025-11' AND application_x = 'ALL Universe'
  GROUP BY 1
)
SELECT
  s.channel_y, s.active_users_xy, au.total_users, p.panel_size,
  ROUND(SAFE_DIVIDE(s.active_users_xy, au.total_users) * 100, 1) AS reach_pct,
  ROUND(SAFE_DIVIDE(s.active_users_xy, p.panel_size)  * 100, 1) AS pop_reach_pct
FROM social s CROSS JOIN app_users au CROSS JOIN panel p
ORDER BY reach_pct DESC;`,
    sanity: [
      { check: 'reach_pct between 0 and 100', expected: '0–100%', violation: '>100% for ALL Universe = category filter still applied to denominator; ALL Universe in get_social_reach must use no category filter' },
      { check: 'active_users_xy ≤ total_app_users (ALL Universe)', expected: 'Social overlap ≤ any-app users', violation: 'Still >100%? Check if omni_cross_social ALL Universe rows are scoped per-category in the source table' },
      { check: 'panel_size identical across all channels in same month', expected: 'Same scalar (CROSS JOIN)', violation: 'Different panel values per channel = panel query filtered incorrectly' },
      { check: 'Category total_app_users > any single brand total_app_users', expected: 'Always', violation: 'A brand denominator exceeds category denominator — wrong app name or wrong category filter on brand query' },
      { check: 'Section ② Brand A and Brand B reach values consistent with Section ①', expected: 'Same API endpoint / same month', violation: 'Discrepancy = different month or category applied to Section ② call' },
    ],
    crossRef: ['Opportunity Index by Channel', 'Panel Size', 'Share & Brand Penetration'],
  },

  {
    id: 'channel-opp-index',
    label: 'Opportunity Index by Channel',
    group: 'Channel Preferences',
    what: 'Ranks social channels by the incremental reach opportunity for the selected brand. Opportunity Score = Category Reach % − Brand Reach %. High score = category has strong reach on a platform but the brand is under-represented — the highest-leverage channel to invest in. Brand Index < 100 signals under-coverage. Extracted from Social Channel Reach into its own page.',
    formulas: [
      { label: 'Opportunity Score', lhs: 'opportunity_score', rhs: 'category_reach_pct − brand_reach_pct', note: 'sorted descending; teal ≥ 5 pp, blue 0–5 pp, red negative (brand over-indexes)' },
      { label: 'Brand Index', lhs: 'brand_index', rhs: '(brand_reach_pct / category_reach_pct) × 100', note: '< 100 = under-coverage vs category; > 100 = brand over-indexes' },
      { label: 'Reach % (same as Social)', lhs: 'reach_pct', rhs: 'active_users_xy / total_app_users × 100' },
    ],
    api: { path: '/api/v1/social/reach', params: [{ name: 'month_id', value: 'YYYY-MM' }, { name: 'category', value: 'Ride Hailing' }, { name: 'application', value: 'ALL Universe', note: 'also called with brand name; called 2× per page load' }] },
    sql: `-- Opportunity Score: category reach − brand reach per channel
WITH app_users_cat AS (
  SELECT SUM(active_users) AS total_users
  FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_age\`
  WHERE month_id = '2025-11' AND session_type = 'open_app'
    AND application = 'Any App'
    -- no category filter for ALL Universe
),
app_users_brand AS (
  SELECT SUM(active_users) AS total_users
  FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_age\`
  WHERE month_id = '2025-11' AND session_type = 'open_app'
    AND application = 'Uber' AND category = 'Ride Hailing'
),
social_cat AS (
  SELECT channel_y, SUM(active_users_xy) AS users_xy
  FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_social_overlap\`
  WHERE month_id = '2025-11' AND application_x = 'ALL Universe'
  GROUP BY 1
),
social_brand AS (
  SELECT channel_y, SUM(active_users_xy) AS users_xy
  FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_social_overlap\`
  WHERE month_id = '2025-11' AND application_x = 'Uber'
  GROUP BY 1
)
SELECT
  c.channel_y,
  ROUND(SAFE_DIVIDE(c.users_xy, ac.total_users) * 100, 1) AS cat_reach_pct,
  ROUND(SAFE_DIVIDE(b.users_xy, ab.total_users) * 100, 1) AS brand_reach_pct,
  ROUND(SAFE_DIVIDE(b.users_xy, ab.total_users) * 100, 1)
    - ROUND(SAFE_DIVIDE(c.users_xy, ac.total_users) * 100, 1) AS reach_gap,
  ROUND(SAFE_DIVIDE(c.users_xy, ac.total_users) * 100, 1)
    - ROUND(SAFE_DIVIDE(b.users_xy, ab.total_users) * 100, 1) AS opportunity_score,
  ROUND(SAFE_DIVIDE(
    SAFE_DIVIDE(b.users_xy, ab.total_users),
    SAFE_DIVIDE(c.users_xy, ac.total_users)
  ) * 100, 0) AS brand_index
FROM social_cat c
JOIN social_brand b USING (channel_y)
CROSS JOIN app_users_cat ac
CROSS JOIN app_users_brand ab
ORDER BY opportunity_score DESC;`,
    sanity: [
      { check: 'Opportunity Score = category_reach_pct − brand_reach_pct exactly', expected: 'Exact arithmetic (after rounding)', violation: 'Rounding error > 0.2 pp = toFixed(1) applied at different stages in frontend vs SQL' },
      { check: 'Brand Index = brand_reach / category_reach × 100', expected: 'Numeric range 0–500+', violation: 'brand_index > 200 for a major channel is suspicious — check denominator (category reach should always be higher for most channels)' },
      { check: 'Top-ranked channel has positive opportunity_score', expected: 'At least one channel should show headroom', violation: 'All negative = brand reaches more than category on every platform — unusual; check that ALL Universe denominator is not category-scoped' },
      { check: 'Channels in table match channels in Social Channel Reach for same brand/month', expected: 'Same set of channels from same API endpoint', violation: 'Mismatch = one page filtered out channels the other kept (channel alignment logic differs)' },
      { check: 'reach_pct values here match Social Channel Reach for same brand+month', expected: 'Identical (same API, same params)', violation: 'Different values = cached stale data or different month applied per page' },
    ],
    crossRef: ['Social Channel Reach', 'Panel Size', 'Share & Brand Penetration'],
  },

  {
    id: 'growth',
    label: 'Growth Opportunities',
    group: 'Growth',
    what: 'Side-by-side penetration of brand and category for each segment. Highlights where the brand under-penetrates vs the category — candidate segments for growth investment.',
    formulas: [
      { label: 'Category seg penetration', lhs: 'cat_penetration_pct', rhs: 'cat_seg_users / seg_panel_size × 100' },
      { label: 'Brand seg penetration', lhs: 'brand_penetration_pct', rhs: 'brand_seg_users / seg_panel_size × 100' },
      { label: 'Opportunity users', lhs: 'opportunity_users', rhs: 'GREATEST(cat_seg_users − brand_seg_users, 0)', note: 'clipped at 0 — never negative' },
      { label: 'Opportunity %', lhs: 'opportunity_pct', rhs: 'opportunity_users / seg_panel_size × 100', note: 'percentage-point gap between category and brand penetration' },
    ],
    api: { path: '/api/v1/growth/opportunities-by-age', params: [{ name: 'category', value: 'Ride Hailing' }, { name: 'session_type', value: 'open_app' }, { name: 'month_id', value: 'YYYY-MM' }, { name: 'application', value: 'Uber', note: 'required — the brand to benchmark against the category' }] },
    sql: `-- Growth opportunity by age: category vs one brand (replace 'Uber' with your brand)
WITH panel AS (
  SELECT attribute_value AS age_band,
    ARRAY_AGG(rolling_30d_unique_customers ORDER BY visited_date DESC LIMIT 1)[OFFSET(0)] AS seg_panel
  FROM \`tsi-explorer.vmo2_database_uk.omni_panel_size\`
  WHERE DATE_TRUNC(visited_date, MONTH) = DATE('2025-11-01') AND attribute = 'age_band'
  GROUP BY 1
),
cat AS (
  SELECT age AS age_band, SUM(active_users) AS cat_users
  FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_age\`
  WHERE month_id = '2025-11' AND session_type = 'open_app'
    AND application = 'Any App' AND category = 'Ride Hailing'
  GROUP BY 1
),
brand AS (
  SELECT age AS age_band, SUM(active_users) AS brand_users
  FROM \`tsi-explorer.vmo2_database_uk.eur_omni__monthly_by_age\`
  WHERE month_id = '2025-11' AND session_type = 'open_app'
    AND application = 'Uber' AND category = 'Ride Hailing'
  GROUP BY 1
)
SELECT
  c.age_band,
  p.seg_panel                                                     AS universe,
  c.cat_users,
  ROUND(SAFE_DIVIDE(c.cat_users, p.seg_panel) * 100, 2)          AS cat_penetration_pct,
  COALESCE(b.brand_users, 0)                                      AS brand_users,
  ROUND(SAFE_DIVIDE(COALESCE(b.brand_users,0), p.seg_panel)*100, 2) AS brand_penetration_pct,
  GREATEST(c.cat_users - COALESCE(b.brand_users,0), 0)            AS opportunity_users,
  ROUND(SAFE_DIVIDE(
    GREATEST(c.cat_users - COALESCE(b.brand_users,0), 0), p.seg_panel
  ) * 100, 2)                                                     AS opportunity_pct
FROM cat c
JOIN panel p USING (age_band)
LEFT JOIN brand b USING (age_band)
ORDER BY c.cat_users DESC;`,
    sanity: [
      { check: 'brand_penetration_pct ≤ cat_penetration_pct for all segments', expected: 'Always', violation: 'Brand penetrates more than the whole category = category filter or deduplication error' },
      { check: 'opportunity_users ≥ 0 always', expected: 'Clipped at 0 by GREATEST()', violation: 'Negative value = GREATEST() not applied in source query' },
      { check: 'Growth page segment penetrations match corresponding Brand Penetration pages', expected: 'Identical values (same SQL logic)', violation: 'Discrepancy = one of the pages uses a different session_type, month, or application name' },
    ],
    crossRef: ['Category Penetration', 'Brand Penetration', 'Share & Brand Penetration'],
  },
];

const GROUPS = ['Category', 'Brand', 'Competition', 'Channel Preferences', 'Growth'];

// ── Main view ─────────────────────────────────────────────────────────────────

export default function ValidationView() {
  const [active, setActive] = useState(SECTIONS[0].id);
  const sec = SECTIONS.find(s => s.id === active);

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0, background: BASE }}>

      {/* ── Left nav ──────────────────────────────────────────────────────── */}
      <div style={{
        width: 220, flexShrink: 0, borderRight: `1px solid ${BORDER}`,
        overflowY: 'auto', padding: '20px 0', background: CARD,
      }}>
        <div style={{ padding: '0 16px 12px', fontSize: 11, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: MUTED }}>
          Dashboard Pages
        </div>
        {GROUPS.map(grp => (
          <div key={grp}>
            <div style={{ padding: '8px 16px 4px', fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: MUTED }}>
              {grp}
            </div>
            {SECTIONS.filter(s => s.group === grp).map(s => (
              <button
                key={s.id}
                onClick={() => setActive(s.id)}
                style={{
                  width: '100%', textAlign: 'left', background: active === s.id ? TEAL_BG : 'none',
                  border: 'none', borderLeft: active === s.id ? `3px solid ${TEAL}` : '3px solid transparent',
                  padding: '7px 16px', fontSize: 13, cursor: 'pointer',
                  color: active === s.id ? TEAL : TEXT, fontWeight: active === s.id ? 600 : 400,
                  transition: 'all .12s',
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        ))}
      </div>

      {/* ── Detail panel ──────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '32px 40px' }}>
        {sec && (
          <>
            {/* Header */}
            <div style={{ marginBottom: 28 }}>
              <SectionTag>{sec.group}</SectionTag>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT, marginTop: 8, marginBottom: 8 }}>
                {sec.label}
              </h1>
              <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.65, maxWidth: 700, margin: 0 }}>
                {sec.what}
              </p>
            </div>

            {/* Cross-references */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: MUTED, marginBottom: 6 }}>
                Related pages
              </div>
              <CrossRef refs={sec.crossRef} />
            </div>

            {/* Formulas */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 4 }}>Key formulas</div>
              <FormulaBox rows={sec.formulas} />
            </div>

            {/* API endpoint */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 4 }}>API spot-check</div>
              <p style={{ fontSize: 13, color: MUTED, marginBottom: 0, lineHeight: 1.6 }}>
                Call this endpoint directly (use a Firebase ID token from DevTools → Network → any request → Authorization header).
                Inspect the returned <code style={{ background: TEAL_BG, color: TEAL, padding: '1px 5px', borderRadius: 3, fontSize: 12 }}>data</code> array
                and manually verify the formula above against the raw field values.
              </p>
              <ApiLine path={sec.api.path} params={sec.api.params} />
            </div>

            {/* BigQuery SQL */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 4 }}>BigQuery verification query</div>
              <p style={{ fontSize: 13, color: MUTED, marginBottom: 0, lineHeight: 1.6 }}>
                Run in the <strong>tsi-explorer</strong> project BigQuery console. Replace the date literal with the month you want to spot-check.
                The result should match the corresponding API response exactly (or within floating-point rounding of ±0.1).
              </p>
              <SqlBlock sql={sec.sql} />
            </div>

            {/* Sanity checks */}
            <div style={{ marginBottom: 32 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: TEXT, marginBottom: 4 }}>Sanity constraints</div>
              <p style={{ fontSize: 13, color: MUTED, marginBottom: 8, lineHeight: 1.6 }}>
                These are logical invariants that must hold regardless of the month or category. A violation indicates
                a data pipeline issue, wrong parameter, or query bug — not a real business anomaly.
              </p>
              <SanityTable rows={sec.sanity} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
